import { ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { Lock, AlertCircle } from "lucide-react";
import type { FeatureFlags } from "@/types/features";

interface FeatureGateProps {
  children: ReactNode;
  feature: keyof FeatureFlags;
  fallback?: ReactNode;
  /**
   * If true, show content when flags fail to load (fail-open).
   * If false (default), hide content when flags fail to load (fail-closed).
   * Use fail-open sparingly - only for non-sensitive features.
   */
  failOpen?: boolean;
}

/**
 * Conditionally renders content or an informative fallback based on a feature flag.
 *
 * By default this component fails-closed: if feature flags cannot be loaded it will render a
 * failure UI. Pass `failOpen = true` to render `children` when flags fail to load.
 *
 * @param children - Content to render when the feature is enabled
 * @param feature - Key of the feature flag to check
 * @param fallback - Optional UI to render when the feature is explicitly disabled
 * @param failOpen - If true, render `children` when feature flags fail to load (default: false)
 * @returns The rendered UI: `children` when the feature is enabled (or allowed by `failOpen`),
 *          the `fallback` when the feature is disabled, or a loading/error/status card otherwise.
 */
export function FeatureGate({ children, feature, fallback, failOpen = false }: FeatureGateProps) {
  const { data: featureFlags, isLoading, error } = useFeatureFlags();

  // Show loading state while flags are being fetched
  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Handle error state - fail-closed by default for security
  if (error || !featureFlags) {
    if (failOpen) {
      // Only fail-open if explicitly requested
      return <>{children}</>;
    }

    // Fail-closed: show error message when flags can't be loaded
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <Card className="border-amber-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="h-5 w-5" />
              Unable to Load Feature
            </CardTitle>
            <CardDescription>We couldn't verify your access to this feature.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Please refresh the page or contact support if the problem persists.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if feature is enabled
  if (featureFlags[feature] === false) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Feature Not Available
            </CardTitle>
            <CardDescription>
              This feature is not available in your current environment.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Please contact us at support@earlycore.dev if you believe you should have access to
              this feature.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
